import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { APIProvider, Map, AdvancedMarker, InfoWindow } from '@vis.gl/react-google-maps';
import { Radio, Users, MapPin, Clock, X as CloseIcon, RefreshCw, Filter, Search } from 'lucide-react';
import { reverseGeocode } from '../../lib/google';
import useAutoRefresh from '../../lib/useAutoRefresh';
import SearchableSelect from '../ui/SearchableSelect';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// 3-state colour buckets for the Live Map marker. Priority (top wins):
//   1. off_site_idle  → checked-in (no visit/break/permission) AND outside their
//                       home geofence AND not moving → RED.
//   2. moving         → average speed of the last few GPS pings ≥ 1 km/h → GREEN.
//   3. stationary     → everything else (on-site, on visit, on break, etc.) → WHITE.
const STATE_META = {
  off_site_idle: { color: '#ef4444', textColor: '#fff', dot: '#fca5a5', label: 'Off-site (no schedule)' },  // red
  moving:        { color: '#10b981', textColor: '#fff', dot: '#34d399', label: 'Moving' },                  // green
  stationary:    { color: '#ffffff', textColor: '#374151', dot: '#9ca3af', label: 'Stationary' },           // white
};

// Movement buckets — speed-of-motion derived from trail timestamps.
// Driving > 30 km/h · Commuting 5–30 km/h · Walking 1–5 km/h · Stationary <1 km/h.
const MOVEMENT = {
  driving:    { label: 'Driving',    short: 'Drv', color: '#ef4444' },
  commuting:  { label: 'Commuting',  short: 'Cmt', color: '#f59e0b' },
  walking:    { label: 'On the move', short: 'Mov', color: '#10b981' },
  stationary: { label: 'Stationary', short: 'Stn', color: '#6b7280' },
};

// Per-status meta for the side-panel staff list + the StatusPill component.
// Lifted to module scope so the side-panel list can also reference it
// (used to crash here with `ReferenceError: STATUS_META is not defined`).
const STATUS_META = {
  on_visit:      { label: 'On Visit',     color: '#10b981' },
  on_break:      { label: 'On Break',     color: '#f59e0b' },
  on_permission: { label: 'Permission',   color: '#3b82f6' },
  checked_in:    { label: 'Checked-in',   color: '#6b7280' },
};

const bucketForSpeed = (kmh) => {
  if (kmh >= 30) return 'driving';
  if (kmh >= 5)  return 'commuting';
  if (kmh >= 1)  return 'walking';
  return 'stationary';
};

// Haversine — distance between two lat/lng points, in km.
const haversineKm = (a, b) => {
  const R = 6371;
  const toRad = (d) => d * Math.PI / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat), lat2 = toRad(b.lat);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
};

// Speed (km/h) between two trail points; returns 0 if dt is zero/invalid.
const segmentKmh = (a, b) => {
  if (!a?.t || !b?.t) return 0;
  const dt = (new Date(b.t).getTime() - new Date(a.t).getTime()) / 1000; // seconds
  if (!Number.isFinite(dt) || dt <= 0) return 0;
  const km = haversineKm({ lat: a.lat, lng: a.lng }, { lat: b.lat, lng: b.lng });
  const kmh = (km / dt) * 3600;
  return Number.isFinite(kmh) ? kmh : 0;
};

// Smoothed current movement — average of the most recent up-to-3 segments
// to neutralise GPS jitter that happens within a single step.
const currentMovement = (staff) => {
  const trail = (staff.trail || []).slice();
  // Make sure the marker head is included.
  const head = { lat: staff.latitude, lng: staff.longitude, t: staff.last_seen };
  const last = trail[trail.length - 1];
  if (!last || last.lat !== head.lat || last.lng !== head.lng) trail.push(head);
  if (trail.length < 2) return { kmh: 0, bucket: 'stationary' };

  const segs = [];
  for (let i = trail.length - 1; i > 0 && segs.length < 3; i--) {
    segs.push(segmentKmh(trail[i - 1], trail[i]));
  }
  const avg = segs.reduce((s, x) => s + x, 0) / segs.length;
  return { kmh: avg, bucket: bucketForSpeed(avg) };
};

const fmt = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }) : '—';
const since = (iso) => {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ${m % 60}m ago`;
};

const META_DEFAULT = STATE_META.stationary;

// Map a live-staff row to one of our 3 colour buckets.
const stateForStaff = (staff) => {
  const movement = currentMovement(staff);
  const moving = movement.bucket !== 'stationary';
  const noSchedule = staff.status === 'checked_in';
  // inside_geofence is null when staff has no home geofence assigned —
  // treat that as "inside" so we don't paint everyone red.
  const outside = staff.inside_geofence === false;
  if (noSchedule && outside && !moving) return 'off_site_idle';
  if (moving) return 'moving';
  return 'stationary';
};

export default function LiveMap() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [selectedAddr, setSelectedAddr] = useState(null);
  const [tick, setTick] = useState(0);
  const [lastRefresh, setLastRefresh] = useState(null);

  // Filters — none selected = show all
  const [geoFilter, setGeoFilter] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [desigFilter, setDesigFilter] = useState('');
  const [nameQuery, setNameQuery] = useState('');
  // Status / activity filter — empty means "any". Used to load only a slice
  // of staff (inside/outside geofence, on visit, etc.) so the map doesn't
  // melt with hundreds of markers.
  const [statusFilter, setStatusFilter] = useState('');
  // Deferred load: map renders ONLY after the admin picks at least one
  // filter and clicks "Display". Prevents the heavy initial fetch + render
  // on first paint when team size is large.
  const [displayed, setDisplayed] = useState(false);

  // Filter master-data dropdowns
  const [geoOptions, setGeoOptions] = useState([]);
  const [deptOptions, setDeptOptions] = useState([]);
  const [desigOptions, setDesigOptions] = useState([]);

  // ===== reverse-geocode the selected pin =====
  useEffect(() => {
    if (!selected) { setSelectedAddr(null); return; }
    let alive = true;
    setSelectedAddr('Looking up address…');
    reverseGeocode(selected.latitude, selected.longitude).then(r => {
      if (alive) setSelectedAddr(r?.formatted || null);
    });
    return () => { alive = false; };
  }, [selected]);

  // ===== load filter master-data once =====
  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };
    Promise.all([
      fetch(`${API_URL}/api/geofences`, { headers }).then(r => r.ok ? r.json() : []),
      fetch(`${API_URL}/api/departments`, { headers }).then(r => r.ok ? r.json() : []),
      fetch(`${API_URL}/api/designations`, { headers }).then(r => r.ok ? r.json() : []),
    ]).then(([g, d, dg]) => {
      setGeoOptions(g || []);
      setDeptOptions(d || []);
      setDesigOptions(dg || []);
    }).catch(() => {});
  }, []);

  // ===== live locations fetcher (silent) =====
  const fetchLocations = useCallback(async (silent = false) => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/admin/live-locations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (r.ok) {
        const data = await r.json();
        setLocations(Array.isArray(data) ? data : []);
        setLastRefresh(new Date());
      }
    } catch {}
    finally {
      if (!silent) setLoading(false);
      setTick(t => t + 1);
    }
  }, []);

  // Initial load — DEFERRED. The map starts empty until the admin picks at
  // least one filter and presses "Display". This avoids fetching+rendering
  // every active staff (hundreds, on large rollouts) the moment the tab
  // mounts, which used to make the map appear stuck on "Loading…".
  useEffect(() => { setLoading(false); }, []);

  // Auto-refresh every 30s while the map is visible AND already displayed.
  useAutoRefresh(() => { if (displayed) fetchLocations(true); }, 7_000);

  // ===== client-side filtering =====
  const filtered = useMemo(() => {
    const q = nameQuery.trim().toLowerCase();
    return locations.filter(l => {
      if (geoFilter && l.geo_fence_location_id !== geoFilter) return false;
      if (deptFilter && l.department_id !== deptFilter) return false;
      if (desigFilter && l.designation_id !== desigFilter) return false;
      if (statusFilter === 'inside'        && l.inside_geofence !== true)  return false;
      if (statusFilter === 'outside'       && l.inside_geofence !== false) return false;
      if (statusFilter === 'on_visit'      && l.status !== 'on_visit')     return false;
      if (statusFilter === 'on_break'      && l.status !== 'on_break')     return false;
      if (statusFilter === 'on_permission' && l.status !== 'on_permission') return false;
      if (q && !(
        (l.name || '').toLowerCase().includes(q) ||
        (l.staff_code || '').toLowerCase().includes(q) ||
        (l.mobile || '').toLowerCase().includes(q)
      )) return false;
      return true;
    });
  }, [locations, geoFilter, deptFilter, desigFilter, statusFilter, nameQuery]);

  const center = useMemo(() => {
    if (!filtered.length) return { lat: 11.0572, lng: 77.3400 };
    const lat = filtered.reduce((s, l) => s + l.latitude, 0) / filtered.length;
    const lng = filtered.reduce((s, l) => s + l.longitude, 0) / filtered.length;
    return { lat, lng };
  }, [filtered]);

  // Drop selection if filters hide it
  useEffect(() => {
    if (selected && !filtered.find(f => f.staff_id === selected.staff_id)) {
      setSelected(null);
    }
  }, [filtered, selected]);

  const clearFilters = () => {
    setGeoFilter(''); setDeptFilter(''); setDesigFilter(''); setNameQuery(''); setStatusFilter('');
    setDisplayed(false);
    setLocations([]);
  };

  const activeFilterCount = (
    (geoFilter ? 1 : 0) + (deptFilter ? 1 : 0) + (desigFilter ? 1 : 0) +
    (nameQuery ? 1 : 0) + (statusFilter ? 1 : 0)
  );

  // At least ONE filter must be picked before the admin can hit Display.
  // (Or they can choose "All staff" by selecting the status "all"
  // explicitly — see the dropdown — which also satisfies activeFilterCount.)
  const canDisplay = activeFilterCount > 0;

  const handleDisplay = () => {
    if (!canDisplay) return;
    setDisplayed(true);
    fetchLocations(false);
  };

  return (
    <div className="h-full" data-testid="live-map-page">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Radio size={22} className="mr-2 text-emerald-600 animate-pulse" />
            Live Staff Locations
          </h1>
          <p className="text-xs text-gray-500 mt-1" data-testid="live-map-meta">
            <Users size={12} className="inline mr-1" />
            {displayed
              ? <>{filtered.length} of {locations.length} live · auto-refresh 30s
                {lastRefresh && <> · last refreshed {since(lastRefresh.toISOString())}</>}</>
              : <>Pick a filter, then press <strong>Display</strong> to load the map.</>}
          </p>
        </div>
        <button
          onClick={() => fetchLocations(false)}
          disabled={!displayed}
          className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm ${
            displayed
              ? 'bg-blue-600 hover:bg-blue-700 text-white'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
          data-testid="live-map-refresh"
          title={displayed ? 'Refresh now' : 'Press Display first to load the map'}
        >
          <RefreshCw size={14} /><span>Refresh</span>
        </button>
      </div>

      {/* Filter bar */}
      <div
        className="bg-white border border-gray-200 rounded-xl p-3 mb-3 flex flex-wrap items-center gap-2"
        data-testid="live-map-filters"
      >
        <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 mr-1">
          <Filter size={12} /> Filters
          {activeFilterCount > 0 && (
            <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full text-[10px]">
              {activeFilterCount}
            </span>
          )}
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm bg-white"
          data-testid="filter-status"
        >
          <option value="">— Status —</option>
          <option value="all">All staff</option>
          <option value="inside">Inside geofence</option>
          <option value="outside">Outside geofence</option>
          <option value="on_visit">On Visit</option>
          <option value="on_break">On Break</option>
          <option value="on_permission">On Permission</option>
        </select>

        <SearchableSelect
          value={geoFilter}
          onChange={setGeoFilter}
          options={[{ value: '', label: 'All locations' }, ...geoOptions.map(g => ({ value: g.id, label: g.name }))]}
          placeholder="All locations"
          testid="filter-geo"
          allowClear
          minWidth={170}
        />

        <SearchableSelect
          value={deptFilter}
          onChange={setDeptFilter}
          options={[{ value: '', label: 'All departments' }, ...deptOptions.map(d => ({ value: d.id, label: d.name }))]}
          placeholder="All departments"
          testid="filter-department"
          allowClear
          minWidth={170}
        />

        <SearchableSelect
          value={desigFilter}
          onChange={setDesigFilter}
          options={[{ value: '', label: 'All designations' }, ...desigOptions.map(d => ({ value: d.id, label: d.name }))]}
          placeholder="All designations"
          testid="filter-designation"
          allowClear
          minWidth={170}
        />

        <div className="relative flex-1 min-w-[180px]">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={nameQuery}
            onChange={(e) => setNameQuery(e.target.value)}
            placeholder="Search name, staff ID or mobile…"
            className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-sm"
            data-testid="filter-name"
          />
        </div>

        {activeFilterCount > 0 && (
          <button
            type="button"
            onClick={clearFilters}
            className="text-xs text-blue-600 hover:text-blue-800 underline px-1"
            data-testid="filter-clear"
          >
            Clear
          </button>
        )}

        <button
          type="button"
          onClick={handleDisplay}
          disabled={!canDisplay}
          className={`ml-auto px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
            canDisplay
              ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
          data-testid="filter-display-btn"
          title={canDisplay ? 'Load filtered staff on the map' : 'Pick at least one filter first'}
        >
          {displayed ? 'Reload' : 'Display'}
        </button>
      </div>

      {/* Marker-colour legend — 3-state model */}
      <div className="flex flex-wrap items-center gap-3 text-[11px] text-gray-500 mb-2 px-1" data-testid="live-map-legend">
        <span className="font-semibold uppercase tracking-wide">Marker:</span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded-full border border-gray-400" style={{ backgroundColor: '#ffffff' }} />
          Stationary
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: '#10b981' }} />
          Moving
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: '#ef4444' }} />
          Off-site, no schedule
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3" style={{ minHeight: 'calc(100vh - 280px)' }}>
        {/* Map */}
        <div className="md:col-span-3 bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="live-map-container">
          {!displayed ? (
            <div className="p-12 text-center text-gray-500" data-testid="live-map-prompt">
              <Filter size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="font-semibold text-gray-700">Pick a filter, then press Display</p>
              <p className="text-xs text-gray-500 mt-2 max-w-sm mx-auto">
                Choose a <strong>Status</strong> (Inside / Outside / On Visit / On Break / On Permission)
                or pick a <strong>Geofence</strong>, <strong>Department</strong>, <strong>Designation</strong>
                or type a <strong>Staff name</strong>. The map will load only the matching team, so it stays fast even with hundreds of users.
              </p>
            </div>
          ) : (
          <>
          {loading && <div className="p-8 text-center text-gray-500">Loading…</div>}
          {!loading && MAPS_KEY ? (
            filtered.length === 0 ? (
              <div className="p-12 text-center text-gray-500" data-testid="live-map-empty">
                <Users size={40} className="mx-auto text-gray-300 mb-3" />
                <p className="font-semibold">
                  {locations.length === 0
                    ? 'No on-duty staff right now'
                    : 'No staff match the current filters'}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {locations.length === 0
                    ? 'Live shows checked-in staff with a GPS ping in the last 30 minutes.'
                    : 'Try clearing filters above.'}
                </p>
              </div>
            ) : (
            <APIProvider apiKey={MAPS_KEY}>
              <Map
                key={tick}
                defaultCenter={center}
                defaultZoom={filtered.length > 1 ? 11 : 13}
                mapId="carloo-live-map"
                gestureHandling="greedy"
                style={{ width: '100%', height: '100%' }}
              >
                {filtered.map(l => {
                  const state = stateForStaff(l);
                  const meta = STATE_META[state];
                  return (
                    <AdvancedMarker
                      key={l.staff_id}
                      position={{ lat: l.latitude, lng: l.longitude }}
                      onClick={() => setSelected(l)}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer' }}>
                        <div style={{
                          width: 36, height: 36, borderRadius: '50%',
                          backgroundColor: meta.color,
                          border: `4px solid ${state === 'stationary' ? '#9ca3af' : 'white'}`,
                          boxShadow: `0 4px 14px ${meta.color}66`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: meta.textColor, fontWeight: 800, fontSize: 14, position: 'relative'
                        }}>
                          {(l.name || '?').charAt(0).toUpperCase()}
                          <span style={{
                            position: 'absolute', top: -3, right: -3,
                            width: 12, height: 12, borderRadius: '50%',
                            backgroundColor: meta.dot, border: '2px solid white',
                            animation: 'pulse 2s infinite'
                          }} />
                        </div>
                        <div style={{
                          marginTop: 2, padding: '2px 8px', backgroundColor: 'white',
                          border: `1px solid ${meta.color === '#ffffff' ? '#d1d5db' : meta.color}`,
                          borderRadius: 4,
                          fontSize: 11, fontWeight: 700,
                          color: meta.color === '#ffffff' ? '#374151' : meta.color,
                          whiteSpace: 'nowrap', boxShadow: '0 1px 3px rgba(0,0,0,0.15)'
                        }}>{l.name}</div>
                      </div>
                    </AdvancedMarker>
                  );
                })}

                {selected && (
                  <InfoWindow
                    position={{ lat: selected.latitude, lng: selected.longitude }}
                    onCloseClick={() => setSelected(null)}
                  >
                    <div className="text-xs" style={{ minWidth: 160 }}>
                      <strong style={{ color: STATE_META[stateForStaff(selected)].color === '#ffffff' ? '#374151' : STATE_META[stateForStaff(selected)].color }}>
                        {selected.name}
                      </strong>
                      <div className="text-gray-600">{STATE_META[stateForStaff(selected)].label}</div>
                      {(() => {
                        const m = currentMovement(selected);
                        return (
                          <div className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full"
                            style={{ backgroundColor: `${MOVEMENT[m.bucket].color}1A`, color: MOVEMENT[m.bucket].color, fontWeight: 600 }}>
                            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: MOVEMENT[m.bucket].color }} />
                            {MOVEMENT[m.bucket].label} · {m.kmh.toFixed(1)} km/h
                          </div>
                        );
                      })()}
                      {selected.supplier_name && <div className="mt-1">{selected.supplier_name}</div>}
                      <div className="text-gray-500 mt-0.5">last seen {since(selected.last_seen)}</div>
                    </div>
                  </InfoWindow>
                )}
              </Map>
            </APIProvider>
            )
          ) : !loading ? (
            <div className="p-8 text-center text-gray-500">No Maps API key configured.</div>
          ) : null}
          </>
          )}
        </div>

        {/* Side panel — staff list / selected detail */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 overflow-y-auto" data-testid="live-map-side" style={{ maxHeight: 'calc(100vh - 280px)' }}>
          {selected ? (
            <div data-testid="live-map-detail">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold">{selected.name}</h3>
                <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-700"><CloseIcon size={16} /></button>
              </div>
              <p className="text-xs text-gray-500 mb-2">{selected.staff_code} · {selected.mobile}</p>
              <div className="flex items-center gap-1 mb-3 flex-wrap">
                <StatusPill status={selected.status} />
                <MovementPill staff={selected} />
              </div>

              {selected.supplier_name && (
                <Row icon={<MapPin size={14} className="text-purple-600" />} label="Visiting" value={selected.supplier_name} />
              )}
              <Row
                icon={<MapPin size={14} className="text-emerald-700" />}
                label="Current address"
                value={selectedAddr || `${selected.latitude.toFixed(5)}, ${selected.longitude.toFixed(5)}`}
              />
              {selected.visit_started && (
                <Row icon={<Clock size={14} className="text-blue-600" />} label="Visit started" value={fmt(selected.visit_started)} />
              )}
              {selected.break_type && (
                <Row icon={<Clock size={14} className="text-amber-600" />} label={`${selected.break_type} break started`} value={fmt(selected.break_started)} />
              )}
              {selected.permission_started && (
                <Row icon={<Clock size={14} className="text-blue-600" />} label="Permission since" value={fmt(selected.permission_started)} />
              )}
              <Row icon={<Radio size={14} className="text-emerald-600" />} label="Last GPS" value={`${fmt(selected.last_seen)} (${since(selected.last_seen)})`} />
              {selected.distance_km > 0 && (
                <Row icon={<MapPin size={14} className="text-orange-600" />} label="Distance" value={`${(selected.distance_km).toFixed?.(1) ?? selected.distance_km} km`} />
              )}
              {selected.department_name && (
                <Row icon={<Users size={14} className="text-gray-500" />} label="Department" value={selected.department_name} />
              )}
              {selected.designation_name && (
                <Row icon={<Users size={14} className="text-gray-500" />} label="Designation" value={selected.designation_name} />
              )}
              {selected.geo_fence_name && (
                <Row icon={<MapPin size={14} className="text-gray-500" />} label="Home location" value={selected.geo_fence_name} />
              )}
              <a
                href={`https://maps.google.com/?q=${selected.latitude},${selected.longitude}`}
                target="_blank" rel="noreferrer"
                className="text-xs text-blue-600 underline mt-3 inline-flex items-center"
              >
                <MapPin size={12} className="mr-1" />Open in Google Maps
              </a>
            </div>
          ) : (
            <div data-testid="live-map-list">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                Active Staff ({filtered.length})
              </p>
              {filtered.length === 0 && (
                <p className="text-sm text-gray-400">
                  {locations.length === 0
                    ? 'No staff are checked-in with recent GPS.'
                    : 'No staff match the current filters.'}
                </p>
              )}
              <div className="space-y-1.5">
                {filtered.map(l => {
                  const meta = STATUS_META[l.status] || META_DEFAULT;
                  const m = currentMovement(l);
                  const mv = MOVEMENT[m.bucket];
                  return (
                    <button
                      key={l.staff_id}
                      type="button"
                      onClick={() => setSelected(l)}
                      className="w-full text-left p-2.5 border rounded-lg hover:bg-gray-50"
                      style={{ borderLeft: `3px solid ${meta.color}` }}
                      data-testid={`live-staff-${l.staff_id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-sm truncate">{l.name}</span>
                        <span className="text-[10px] font-medium whitespace-nowrap" style={{ color: meta.color }}>
                          {since(l.last_seen)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-2 mt-0.5">
                        <p className="text-[11px] text-gray-500 truncate">
                          {meta.label}{l.supplier_name ? ` · ${l.supplier_name}` : ''}
                        </p>
                        <span
                          className="inline-flex items-center gap-1 text-[10px] font-semibold whitespace-nowrap"
                          style={{ color: mv.color }}
                          title={`${mv.label} · ${m.kmh.toFixed(1)} km/h`}
                        >
                          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: mv.color }} />
                          {mv.short} {m.kmh > 0 ? `${m.kmh.toFixed(0)}` : '0'}km/h
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * The trail-polyline rendering used to live here. Removed per spec: the Live
 * Map now shows only the round logo + name pill in one of 3 colours. The
 * speed-bucket helper functions are still used by the per-row Movement pill in
 * the side panel.
 */

function StatusPill({ status }) {
  const meta = STATUS_META[status] || { label: 'Live', color: '#6b7280' };
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full"
      style={{ backgroundColor: `${meta.color}1A`, color: meta.color }}
      data-testid="live-status-pill"
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: meta.color }} />
      {meta.label}
    </span>
  );
}

function MovementPill({ staff }) {
  const m = currentMovement(staff);
  const meta = MOVEMENT[m.bucket];
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full"
      style={{ backgroundColor: `${meta.color}1A`, color: meta.color }}
      data-testid="live-movement-pill"
      title={`Average speed across last ${Math.min((staff.trail || []).length, 3)} pings`}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: meta.color }} />
      {meta.label} · {m.kmh.toFixed(1)} km/h
    </span>
  );
}

function Row({ icon, label, value }) {
  return (
    <div className="flex items-start space-x-2 text-sm py-1.5 border-b border-gray-100 last:border-0">
      <div className="mt-0.5">{icon}</div>
      <div className="flex-1">
        <p className="text-[10px] uppercase tracking-wide text-gray-400">{label}</p>
        <p className="text-gray-800">{value || '—'}</p>
      </div>
    </div>
  );
}
